import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ReportStatistics, ReportWeighting, WeightTarget } from '../../models/end-client-reporting-package';
import { DEFAULT_CAUTION_BASE, DEFAULT_MIN_BASE, WeightCandidate, resolveStatistics } from './crosstab-stats';
import type { PickableVariable } from './report-aggregation';
import type { PartVariable } from './report-design-edit';
import type { WeightingDiagnostics } from './weighting';
import type { ReportSelectOption } from './report-select.component';

/** How far a question's targets can stray from 100 before the panel says so. */
const TARGET_TOLERANCE = 0.05;

/**
 * The deck's statistics, drawn in the Report statistics dialog: how respondents are weighted, and
 * the confidence, minimum base and decimals every table is tested and printed by. Report level
 * because each of these changes every figure, or every mark, in the deck at once.
 *
 * It holds nothing of its own. Every change goes out whole through `statisticsChange`, with the
 * questions a target weighting names so the deck can map them.
 */
@Component({
  selector: 'app-report-statistics',
  standalone: false,
  template: `
    <div class="stats">
      <section class="stats-col" aria-labelledby="stats-weighting-title">
        <h3 class="stats-heading" id="stats-weighting-title">Weighting</h3>
        <p class="stats-note mb-2">Who counts for how much. Changes every figure in the report.</p>
        <p class="sr-only" id="stats-weighting">Weighting method</p>
        <div class="segmented" role="group" aria-labelledby="stats-weighting">
          @for (choice of methods; track choice.value) {
            <button type="button" [attr.aria-pressed]="method === choice.value"
                    (click)="setMethod(choice.value)">{{ choice.text }}</button>
          }
        </div>

        @if (method === 'column') {
          @if (weightOptions.length) {
            <label class="stats-label mt-2" for="stats-weight-column">Weight column</label>
            <or-report-select id="stats-weight-column"
                              [options]="weightOptions"
                              [value]="column"
                              placeholder="Choose a column…"
                              (selectedChange)="setColumn($event)">
            </or-report-select>
          } @else {
            <p class="stats-note mt-2 mb-1">No column in this data looks like a weight. Type its name if you know it.</p>
          }
          <label class="stats-label mt-2" for="stats-weight-other">Or name a column</label>
          <input id="stats-weight-other" type="text" class="form-control form-control-sm"
                 placeholder="e.g. wt_total" [value]="column" (change)="setColumn($any($event.target).value)">
        }

        @if (method === 'targets') {
          <p class="stats-note mt-2 mb-2">
            Respondents are weighted until each question below matches its targets. Weights are set
            on the whole sample, before any filter.
          </p>
          @for (target of targets; track target.variableKey; let t = $index) {
            <div class="target-card mb-2">
              <div class="d-flex align-items-center mb-1">
                <span class="target-name text-truncate">{{ labelFor(target.variableKey) }}</span>
                <button type="button" class="icon-btn ml-auto"
                        [attr.aria-label]="'Stop weighting on ' + labelFor(target.variableKey)"
                        (click)="removeTarget(t)">
                  <i class="fas fa-close" aria-hidden="true"></i>
                </button>
              </div>
              <table class="target-table">
                <caption class="sr-only">Targets for {{ labelFor(target.variableKey) }}</caption>
                <thead>
                  <tr>
                    <th scope="col">Answer</th>
                    <th scope="col">Sample</th>
                    <th scope="col">Target %</th>
                    @if (achieved(target.variableKey)) {
                      <th scope="col">Weighted</th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of sharesOf(target.variableKey); track row.answer) {
                    <tr>
                      <th scope="row">{{ row.answer }}</th>
                      <td>{{ row.percent.toFixed(1) }}</td>
                      <td>
                        <input type="number" min="0" max="100" step="0.1"
                               class="form-control form-control-sm target-input"
                               [attr.aria-label]="'Target for ' + row.answer"
                               [value]="target.shares[row.answer] ?? ''"
                               (change)="setShare(t, row.answer, $any($event.target).value)">
                      </td>
                      @if (achieved(target.variableKey); as achievedRows) {
                        <td [class.off]="isOff(achievedRows, row.answer, target)">{{ achievedOf(achievedRows, row.answer) }}</td>
                      }
                    </tr>
                  }
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Total</th>
                    <td></td>
                    <td [class.off]="!totalsHundred(target)">{{ totalOf(target).toFixed(1) }}</td>
                  </tr>
                </tfoot>
              </table>
              <div class="d-flex flex-wrap mt-1">
                <button type="button" class="link-btn mr-3" (click)="useSample(t)">Use sample %</button>
                <button type="button" class="link-btn mr-3" (click)="spreadEvenly(t)">Spread evenly</button>
              </div>
              @if (!totalsHundred(target)) {
                <p class="stats-warn mt-1 mb-0" role="status">Targets total {{ totalOf(target).toFixed(1) }}%, so they are scaled to 100.</p>
              }
            </div>
          }
          <or-report-select ariaLabel="Weight on another question"
                            [options]="targetOptions"
                            [value]="null"
                            placeholder="+ Weight on a question…"
                            (selectedChange)="addTarget($event)">
          </or-report-select>

          <div class="input-row mt-2">
            <div>
              <label class="stats-label" for="stats-min-weight">Smallest weight</label>
              <input id="stats-min-weight" type="number" min="0" step="0.1" class="form-control form-control-sm"
                     placeholder="No cap" [value]="minWeight ?? ''" (change)="setCap('minWeight', $any($event.target).value)">
            </div>
            <div>
              <label class="stats-label" for="stats-max-weight">Largest weight</label>
              <input id="stats-max-weight" type="number" min="0" step="0.1" class="form-control form-control-sm"
                     placeholder="No cap" [value]="maxWeight ?? ''" (change)="setCap('maxWeight', $any($event.target).value)">
            </div>
          </div>
        }

        @if (diagnostics; as d) {
          <div class="diagnostics mt-2" role="status">
            <div class="d-flex">
              <div class="metric">
                <span class="metric-value" [class.is-poor]="d.efficiency < 0.7">{{ (d.efficiency * 100).toFixed(0) }}%</span>
                <span class="metric-label">Efficiency</span>
              </div>
              <div class="metric">
                <span class="metric-value">{{ d.min.toFixed(2) }}–{{ d.max.toFixed(2) }}</span>
                <span class="metric-label">Weight range</span>
              </div>
              @if (d.converged !== undefined) {
                <div class="metric">
                  <span class="metric-value" [class.is-poor]="!d.converged">{{ d.converged ? 'Met' : 'Missed' }}</span>
                  <span class="metric-label">Targets</span>
                </div>
              }
            </div>
            <p class="stats-note mt-1 mb-0">
              {{ d.weighted }} of {{ d.respondents }} respondents weighted. Efficiency is the share of
              the sample's precision kept: below 70% the weights are working hard.
            </p>
            @for (problem of d.problems; track problem) {
              <p class="stats-warn mt-1 mb-0">{{ problem }}</p>
            }
            <button type="button" class="link-btn mt-1" (click)="downloadWeights.emit()">
              <i class="fas fa-download mr-1" aria-hidden="true"></i>Download the weights
            </button>
          </div>
        }
      </section>

      <section class="stats-col" aria-labelledby="stats-testing-title">
        <h3 class="stats-heading" id="stats-testing-title">Testing &amp; display</h3>
        <p class="stats-note mb-0">How significance is marked and how small bases and figures are shown.</p>

        <p class="stats-label mt-2" id="stats-confidence">Significance confidence</p>
        <div class="segmented" role="group" aria-labelledby="stats-confidence">
          @for (level of levels; track level) {
            <button type="button" [attr.aria-pressed]="resolved.confidenceLevel === level"
                    (click)="emit({ confidenceLevel: level })">{{ level }}%</button>
          }
        </div>

        <p class="stats-label mt-3" id="stats-correction">Many tests on one table</p>
        <div class="segmented" role="group" aria-labelledby="stats-correction">
          @for (choice of corrections; track choice.value) {
            <button type="button" [attr.aria-pressed]="resolved.correction === choice.value"
                    [tooltip]="choice.hint" container="body" containerClass="config-hint-tooltip"
                    (click)="emit({ correction: choice.value === 'none' ? undefined : choice.value })">{{ choice.text }}</button>
          }
        </div>
        <p class="stats-note mt-1 mb-0">{{ correctionHint }}</p>

        <!-- The guardrails, in respondents and never weighted: what a column needs before it is
             tested, before it reads without a warning, and before a cell is shown at all. -->
        <p class="stats-label mt-3">Small bases</p>
        <div class="input-row">
          <div>
            <label class="stats-sub" for="stats-min-base">Too few under</label>
            <input id="stats-min-base" type="number" min="0" class="form-control form-control-sm"
                   [placeholder]="defaultMinBase" [value]="statistics?.minBase ?? ''"
                   (change)="setCount('minBase', $any($event.target).value)">
          </div>
          <div>
            <label class="stats-sub" for="stats-caution-base">Caution under</label>
            <input id="stats-caution-base" type="number" min="0" class="form-control form-control-sm"
                   [placeholder]="defaultCautionBase" [value]="statistics?.cautionBase ?? ''"
                   (change)="setCount('cautionBase', $any($event.target).value)">
          </div>
          <div>
            <label class="stats-sub" for="stats-min-cell">Hide cells under</label>
            <input id="stats-min-cell" type="number" min="0" class="form-control form-control-sm"
                   placeholder="Off" [value]="statistics?.minCellCount || ''"
                   (change)="setCount('minCellCount', $any($event.target).value)">
          </div>
        </div>
        <p class="stats-note mt-1 mb-0">
          Under "too few" a column is greyed, untested and cannot be drilled into; under "caution" it is
          flagged.
        </p>

        <p class="stats-label mt-3" id="stats-decimals">Table decimals</p>
        <div class="segmented" role="group" aria-labelledby="stats-decimals">
          @for (places of decimalChoices; track places) {
            <button type="button" [attr.aria-pressed]="resolved.decimals === places"
                    (click)="emit({ decimals: places })">{{ places === 0 ? '12%' : places === 1 ? '12.3%' : '12.34%' }}</button>
          }
        </div>
      </section>
    </div>
  `,
  styles: [`
    :host { display: block; }
    /* Two columns where there is room: weighting beside testing and display. One under the other on narrow screens. */
    .stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr)); gap: 1.5rem 2rem; }
    .stats-col { min-width: 0; }
    .stats-heading { margin: 0 0 0.125rem; font-size: var(--nav-font14); font-weight: 700; color: var(--nav-grey6); }
    .stats-label {
      display: block; margin: 0.75rem 0 0.25rem;
      font-size: var(--nav-font12); font-weight: 600; color: var(--nav-grey5);
    }
    .stats-note { font-size: var(--nav-font12); color: var(--nav-grey4); }
    .stats-sub { display: block; margin-bottom: 0.125rem; font-size: var(--nav-font10); color: var(--nav-grey4); }
    .stats-warn { font-size: var(--nav-font12); color: var(--nav-error); }

    /* Number fields side by side, sharing the width. min-width: 0 because an input will not shrink
       below its own default width otherwise -- three of them made the dialog wider than a phone. */
    .input-row { display: flex; gap: 0.5rem; }
    .input-row > div { flex: 1 1 0; min-width: 0; }

    /* A phone: everything top to bottom. The two columns stack, the number fields go one under the
       other at full width, and a choice's words may wrap rather than widen its row. */
    @media (max-width: 575.98px) {
      .stats { grid-template-columns: minmax(0, 1fr); }
      .input-row { flex-direction: column; }
      .segmented button { white-space: normal; }
    }

    /* A row of mutually exclusive choices, pressed state drawn as the selected segment. */
    .segmented {
      display: inline-flex; width: 100%;
      border: 1px solid var(--nav-grey1); border-radius: 6px; overflow: hidden;
    }
    .segmented button {
      flex: 1 1 0; padding: 0.25rem 0.5rem; border: 0;
      font-size: var(--nav-font12); color: var(--nav-grey5); background: var(--nav-true-white);
    }
    .segmented button + button { border-left: 1px solid var(--nav-grey1); }
    .segmented button[aria-pressed='true'] {
      color: var(--nav-accent-blue); background: var(--nav-accent-blue-tint); font-weight: 600;
    }
    .segmented button:focus-visible { outline: 2px solid var(--nav-accent-blue); outline-offset: -2px; }

    .target-card { padding: 0.5rem; border: 1px solid var(--nav-grey1); border-radius: 6px; background: var(--nav-snow-white); }
    .target-name { font-size: var(--nav-font12); font-weight: 600; color: var(--nav-grey5); }
    .target-table { width: 100%; font-size: var(--nav-font12); color: var(--nav-grey5); }
    .target-table th, .target-table td { padding: 2px 4px; text-align: right; font-weight: 400; }
    .target-table th:first-child { text-align: left; }
    .target-table thead th { color: var(--nav-grey4); font-weight: 600; }
    .target-table tfoot th, .target-table tfoot td { font-weight: 600; border-top: 1px solid var(--nav-grey1); }
    .target-table .off { color: var(--nav-error); }
    .target-input { width: 4.5rem; margin-left: auto; text-align: right; }

    .icon-btn {
      width: 1.5rem; height: 1.5rem; padding: 0; border: 0; line-height: 1;
      color: var(--nav-grey4); background: transparent;
    }
    .icon-btn:hover, .icon-btn:focus { color: var(--nav-error); }
    .link-btn {
      padding: 0; border: 0; background: transparent;
      font-size: var(--nav-font12); color: var(--nav-accent-blue);
    }
    .link-btn:hover { text-decoration: underline; }

    .diagnostics { padding: 0.5rem; border-radius: 6px; background: var(--nav-accent-blue-tint); }
    .metric { display: flex; flex-direction: column; margin-right: 1rem; }
    .metric-value { font-size: var(--nav-font16); font-weight: 600; color: var(--nav-grey6); }
    .metric-value.is-poor { color: var(--nav-error); }
    .metric-label { font-size: var(--nav-font10); color: var(--nav-grey4); text-transform: uppercase; letter-spacing: 0.04em; }
  `],
})
export class ReportStatisticsComponent {
  @Input() statistics?: ReportStatistics;
  @Input() diagnostics: WeightingDiagnostics | null = null;
  @Input() weightColumns: WeightCandidate[] = [];
  /** Every question in the survey; the single-answer ones can be weighted on. */
  @Input() variables: PickableVariable[] = [];
  /** Each answer's unweighted share of a question, from the deck's records. */
  @Input({ required: true }) sharesOf!: (variableKey: string) => { answer: string; percent: number }[];

  @Output() statisticsChange = new EventEmitter<{ statistics: ReportStatistics; variables: PartVariable[] }>();
  @Output() downloadWeights = new EventEmitter<void>();

  readonly methods: { value: 'none' | 'column' | 'targets'; text: string }[] = [
    { value: 'none', text: 'None' },
    { value: 'column', text: 'Data column' },
    { value: 'targets', text: 'Targets' },
  ];
  readonly levels: (90 | 95 | 99)[] = [90, 95, 99];
  readonly decimalChoices: (0 | 1 | 2)[] = [0, 1, 2];
  readonly defaultMinBase = String(DEFAULT_MIN_BASE);
  readonly defaultCautionBase = String(DEFAULT_CAUTION_BASE);
  readonly corrections: { value: 'none' | 'bonferroni' | 'bh'; text: string; hint: string }[] = [
    {
      value: 'none', text: 'No correction',
      hint: 'Each test is judged on its own at the chosen confidence level. The usual banner-table convention, so figures match tabs from '
        + 'other tools. A big table will have a few marks that are just chance (about 1 in 20 tests at 95%).',
    },
    {
      value: 'bonferroni', text: 'Bonferroni',
      hint: 'Strictest. The threshold is divided by the number of tests in each row -- 10 comparisons at 95% means each must clear 99.5%. '
        + 'Few marks, each very unlikely to be chance, but smaller real differences can be missed. Best when a mark must be watertight.',
    },
    {
      value: 'bh', text: 'B-H',
      hint: 'Benjamini-Hochberg, applied across the whole table. Caps the share of marks that could be chance (about 5% at 95%) while '
        + 'keeping most real differences. Good for big exploratory tables where Bonferroni would remove nearly everything.',
    },
  ];

  get correctionHint(): string {
    switch (this.resolved.correction) {
      case 'bonferroni': return 'Strict: fewer marks, and each one is very unlikely to be chance.';
      case 'bh': return 'Keeps most real differences in a big table while capping the share that are chance.';
      default: return 'Each test stands alone -- the usual banner-table convention, and what other tabs will match.';
    }
  }

  /**
   * Which weighting is being set up. Held here as well as in the design because choosing Targets
   * stores nothing until a question is added -- an empty weighting is no weighting -- and the panel
   * must not snap back to None the moment it is picked.
   */
  private chosenMethod: 'none' | 'column' | 'targets' | null = null;

  get resolved() {
    return resolveStatistics(this.statistics);
  }

  get method(): 'none' | 'column' | 'targets' {
    return this.statistics?.weighting?.method ?? this.chosenMethod ?? 'none';
  }

  get column(): string {
    const weighting = this.statistics?.weighting;
    return weighting?.method === 'column' ? weighting.column : '';
  }

  get weightOptions(): ReportSelectOption[] {
    return this.weightColumns.map(candidate => ({
      value: candidate.column,
      code: candidate.column,
      text: `mean ${candidate.mean.toFixed(2)}, ${candidate.min.toFixed(2)}–${candidate.max.toFixed(2)}`
        + (candidate.weighted < candidate.total ? `, ${candidate.total - candidate.weighted} blank` : ''),
    }));
  }

  get targets(): WeightTarget[] {
    const weighting = this.statistics?.weighting;
    return weighting?.method === 'targets' ? weighting.targets : [];
  }

  get minWeight(): number | undefined {
    const weighting = this.statistics?.weighting;
    return weighting?.method === 'targets' ? weighting.minWeight : undefined;
  }

  get maxWeight(): number | undefined {
    const weighting = this.statistics?.weighting;
    return weighting?.method === 'targets' ? weighting.maxWeight : undefined;
  }

  /** Single-answer questions not already weighted on: only they split the sample into groups. */
  get targetOptions(): ReportSelectOption[] {
    const used = new Set(this.targets.map(target => target.variableKey));
    return this.variables
      .filter(variable => variable.kind === 'coded' && !variable.partOf && !used.has(variable.key))
      .map(variable => ({ value: variable.key, code: variable.position, text: variable.label }));
  }

  labelFor(key: string): string {
    return this.variables.find(variable => variable.key === key)?.label ?? key;
  }

  achieved(variableKey: string): { answer: string; achieved: number }[] | null {
    return this.diagnostics?.targets?.find(target => target.variableKey === variableKey)?.rows ?? null;
  }

  achievedOf(rows: { answer: string; achieved: number }[], answer: string): string {
    const row = rows.find(candidate => candidate.answer === answer);
    return row ? row.achieved.toFixed(1) : '—';
  }

  isOff(rows: { answer: string; achieved: number }[], answer: string, target: WeightTarget): boolean {
    const row = rows.find(candidate => candidate.answer === answer);
    const total = this.totalOf(target);
    const wanted = total > 0 ? ((target.shares[answer] ?? 0) / total) * 100 : 0;
    return !!row && Math.abs(row.achieved - wanted) > 0.5;
  }

  totalOf(target: WeightTarget): number {
    return Object.values(target.shares).reduce((sum, share) => sum + (share > 0 ? share : 0), 0);
  }

  totalsHundred(target: WeightTarget): boolean {
    return Math.abs(this.totalOf(target) - 100) <= TARGET_TOLERANCE;
  }

  /**
   * What each method was last set to, so switching away and back does not lose a set of targets
   * someone typed. Only for as long as the panel is open; what is saved is the method chosen.
   */
  private readonly stashed = new Map<'column' | 'targets', ReportWeighting>();

  setMethod(method: 'none' | 'column' | 'targets') {
    this.chosenMethod = method;
    const current = this.statistics?.weighting;
    if (method === current?.method) return;
    if (current) this.stashed.set(current.method, current);
    this.emit({ weighting: method === 'none' ? undefined : this.stashed.get(method) });
  }

  setColumn(column: string) {
    const trimmed = (column ?? '').trim();
    this.emit({ weighting: trimmed ? { method: 'column', column: trimmed } : undefined });
  }

  addTarget(variableKey: string) {
    if (!variableKey) return;
    // Starts from the sample's own shares: a target nobody has typed yet then changes nothing, and
    // the person adjusts from where the sample is rather than from blank boxes.
    const shares = Object.fromEntries(this.sharesOf(variableKey)
      .map(row => [row.answer, Math.round(row.percent * 10) / 10]));
    this.emitTargets([...this.targets, { variableKey, shares }]);
  }

  removeTarget(index: number) {
    this.emitTargets(this.targets.filter((_, i) => i !== index));
  }

  setShare(index: number, answer: string, value: string) {
    const share = Number.parseFloat(value);
    this.emitTargets(this.targets.map((target, i) => {
      if (i !== index) return target;
      const shares = { ...target.shares };
      if (Number.isFinite(share) && share >= 0) shares[answer] = share;
      else delete shares[answer];
      return { ...target, shares };
    }));
  }

  useSample(index: number) {
    const target = this.targets[index];
    const shares = Object.fromEntries(this.sharesOf(target.variableKey)
      .map(row => [row.answer, Math.round(row.percent * 10) / 10]));
    this.emitTargets(this.targets.map((t, i) => i === index ? { ...t, shares } : t));
  }

  spreadEvenly(index: number) {
    const target = this.targets[index];
    const answers = this.sharesOf(target.variableKey).map(row => row.answer);
    if (!answers.length) return;
    const each = Math.round((100 / answers.length) * 10) / 10;
    const shares = Object.fromEntries(answers.map(answer => [answer, each]));
    this.emitTargets(this.targets.map((t, i) => i === index ? { ...t, shares } : t));
  }

  setCap(cap: 'minWeight' | 'maxWeight', value: string) {
    const weighting = this.statistics?.weighting;
    if (weighting?.method !== 'targets') return;
    const parsed = Number.parseFloat(value);
    const next = { ...weighting };
    if (Number.isFinite(parsed) && parsed > 0) next[cap] = parsed;
    else delete next[cap];
    this.emit({ weighting: next });
  }

  setCount(field: 'minBase' | 'cautionBase' | 'minCellCount', value: string) {
    const parsed = Number.parseInt(value, 10);
    const usable = Number.isFinite(parsed) && parsed >= 0 && !(field === 'minCellCount' && parsed === 0);
    this.emit({ [field]: usable ? parsed : undefined });
  }

  private emitTargets(targets: WeightTarget[]) {
    const weighting = this.statistics?.weighting;
    const caps = weighting?.method === 'targets'
      ? { minWeight: weighting.minWeight, maxWeight: weighting.maxWeight }
      : {};
    this.emit({ weighting: targets.length ? { method: 'targets', targets, ...caps } : undefined });
  }

  emit(change: Partial<ReportStatistics>) {
    const statistics: ReportStatistics = { ...this.statistics, ...change };
    // Absent rather than undefined, so a saved design carries only what was set.
    (Object.keys(statistics) as (keyof ReportStatistics)[])
      .forEach(key => statistics[key] === undefined && delete statistics[key]);
    const weighting: ReportWeighting | undefined = statistics.weighting;
    const variables: PartVariable[] = weighting?.method === 'targets'
      ? weighting.targets.map(target => ({
        key: target.variableKey,
        position: this.variables.find(variable => variable.key === target.variableKey)?.position,
      }))
      : [];
    this.statisticsChange.emit({ statistics, variables });
  }
}
